import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import {
  ThemeEvents,
  CartUpdateEvent,
  CartSyncedEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    this.addEventListener('click', this.#onRemoveClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    this.removeEventListener('click', this.#onRemoveClick);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.#getRowForLine(line);

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Finds the rendered cart-item row for a given Shopify cart line number.
   *
   * Shopify's `line` counts ALL items in `cart.items` including nested children
   * (e.g. secure-shield items that have a `parent_relationship`). However,
   * `refs.cartItemRows` only contains the rendered parent rows (children are
   * skipped via `{% unless item.parent_relationship %}` in cart-products.liquid).
   * So indexing the array by `line - 1` is unsafe whenever a child sits between
   * two parents in the cart — the lookup returns `undefined` and the remove /
   * quantity-change paths silently no-op.
   *
   * Resolving the row through the `data-cart-line` attribute that the Liquid
   * template stamps on the remove button and quantity input keeps the lookup
   * aligned with Shopify's line numbering regardless of nested children.
   *
   * @param {number} line - 1-based Shopify cart line number.
   * @returns {HTMLElement | undefined}
   */
  #getRowForLine(line) {
    return this.refs.cartItemRows.find((row) =>
      row.querySelector(`[data-cart-line="${line}"]`)
    );
  }

  /**
   * Delegated click handler for [data-cart-remove] buttons.
   * Triggers AJAX line-item removal instead of full page navigation.
   * @param {MouseEvent} event
   */
  #onRemoveClick = (event) => {
    const btn = /** @type {HTMLElement|null} */ (event.target)?.closest('[data-cart-remove]');
    if (!btn) return;

    event.preventDefault();
    const line = Number(btn.dataset.cartLine);
    if (!line) return;
    this.onLineItemRemove(line);
  };

  /**
   * Closes the parent cart drawer when the cart becomes empty.
   * morphSection strips the dialog's `open` attribute without calling closeDialog(),
   * which leaves body in position:fixed and scroll-lock on <html>.
   * @param {number} itemCount - The new cart item count after the update.
   */
  async #closeDrawerIfCartEmpty(itemCount) {
    if (itemCount !== 0) return;
    const cartDrawer = /** @type {HTMLElement & { closeDialog?: () => Promise<void> }} */ (
      this.closest('cart-drawer-component')
    );
    if (cartDrawer?.closeDialog) {
      await cartDrawer.closeDialog();
    }
  }

  /**
   * Handles the line item removal.
   * When the removal would leave only free-gift items in the cart,
   * those free gifts are also removed in a single batch request.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    const cartItemRowToRemove = this.#getRowForLine(line);
    if (!cartItemRowToRemove) return;

    const freeGiftRows = this.#getLoneFreeGiftRows(cartItemRowToRemove);

    if (freeGiftRows.length > 0) {
      this.#batchRemoveWithFreeGifts(cartItemRowToRemove, freeGiftRows);
      return;
    }

    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    this.#animateRowRemoval(cartItemRowToRemove);
  }

  /**
   * Returns free-gift rows that would be left alone in the cart after
   * removing the target row and its nested children. Returns an empty
   * array when the remaining cart still contains non-free-gift items.
   * @param {HTMLElement} rowToRemove
   * @returns {HTMLElement[]}
   */
  #getLoneFreeGiftRows(rowToRemove) {
    const allRows = this.refs.cartItemRows;
    const removingKey = rowToRemove.dataset.key;

    const remainingRows = allRows.filter(
      (row) => row !== rowToRemove && row.dataset.parentKey !== removingKey
    );

    if (remainingRows.length === 0) return [];

    const allRemainingAreFreeGifts = remainingRows.every(
      (row) => row.classList.contains('bv-cart-item--free-gift')
    );

    return allRemainingAreFreeGifts ? remainingRows : [];
  }

  /**
   * Removes the target row together with orphaned free-gift rows using
   * a single /cart/update.js request (sets all their quantities to 0).
   * @param {HTMLElement} targetRow
   * @param {HTMLElement[]} freeGiftRows
   */
  #batchRemoveWithFreeGifts(targetRow, freeGiftRows) {
    const allRowsToRemove = [targetRow, ...freeGiftRows];

    /** @type {Record<string, number>} */
    const updates = {};
    for (const row of allRowsToRemove) {
      const key = row.dataset.key;
      if (key) updates[key] = 0;

      // Also zero-out nested children of each row
      for (const child of this.refs.cartItemRows) {
        if (child.dataset.parentKey === key && child.dataset.key) {
          updates[child.dataset.key] = 0;
        }
      }
    }

    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker('clear:user-action');
    this.#disableCartItems();
    this.refs.cartTotal?.shimmer();

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    fetch(`${Theme.routes.cart_update_url}`, fetchConfig('json', {
      body: JSON.stringify({
        updates,
        sections: Array.from(sectionsToUpdate).join(','),
        sections_url: window.location.pathname,
      }),
    }))
      .then((response) => response.text())
      .then(async (responseText) => {
        const parsed = JSON.parse(responseText);
        resetShimmer(this);

        if (parsed.errors) return;

        const newSectionHTML = new DOMParser().parseFromString(
          parsed.sections[this.sectionId],
          'text/html'
        );
        const newCartItemCount = parseInt(
          newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent || '0',
          10
        );

        this.#updateQuantitySelectors(parsed);

        this.dispatchEvent(
          new CartUpdateEvent(parsed, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsed.sections,
          })
        );

        await this.#closeDrawerIfCartEmpty(newCartItemCount);
        
        if(window.location.pathname.includes('cart') || newCartItemCount > 0) {
          morphSection(this.sectionId, parsed.sections[this.sectionId]);
        } else {
          this.#morphCartPartials(parsed.sections[this.sectionId]);
        }

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
        document.dispatchEvent(new CartSyncedEvent(undefined, this.sectionId, { source: 'cart-items-component' }));
      });

    // Animate all rows being removed
    for (const row of allRowsToRemove) {
      this.#animateRowRemoval(row);
    }
  }

  /**
   * Plays the removal animation on a cart item row and its nested children,
   * then removes them from the DOM.
   * @param {HTMLElement} row
   */
  #animateRowRemoval(row) {
    const allRows = [
      row,
      ...this.refs.cartItemRows.filter((r) => r.dataset.parentKey === row.dataset.key),
    ];

    const removeRow = (/** @type {HTMLElement} */ target) => {
      const key = target.dataset.key;
      setTimeout(() => {
        if (!target.isConnected) return;
        if (key != null && target.dataset.key !== key) return;
        target.remove();
      }, 0);
    };

    allRows.forEach((r) => {
      if (prefersReducedMotion()) {
        removeRow(r);
        return;
      }
      r.style.setProperty('--row-height', `${r.clientHeight}px`);
      r.classList.add('removing');
      onAnimationEnd(r, () => removeRow(r));
    });
  }

    /**
   * Morphs only the cart tab button and cart panel, leaving the rest of the
   * drawer section untouched. Use this in place of morphSection when nothing
   * outside #tab-cart / #panel-cart needs to update.
   *
   * Preserves the live cart-progress-bar instance the same way morphSection does,
   * so its internal state (savings, gift toggles) survives the swap.
   *
   * @param {string} html - The new section HTML from the cart-update response.
   */
    #morphCartPartials(html) {
      const newDoc = new DOMParser().parseFromString(html, 'text/html');
  
      const panelCart = document.getElementById('panel-cart');
      const savedProgressBars = panelCart
        ? Array.from(panelCart.querySelectorAll('cart-progress-bar, .bv-cart-progress'))
        : [];
      savedProgressBars.forEach((el) => el.remove());
  
      for (const id of ['tab-cart', 'panel-cart']) {
        const oldEl = document.getElementById(id);
        const newEl = newDoc.getElementById(id);
        if (oldEl && newEl) morph(oldEl, newEl);
      }
  
      if (panelCart) {
        const newProgressBars = panelCart.querySelectorAll('cart-progress-bar, .bv-cart-progress');
        savedProgressBars.forEach((saved, i) => {
          if (newProgressBars[i]) newProgressBars[i].replaceWith(saved);
        });
      }
    }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();
    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then(async (responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        // TODO: below code cause the issue while there are removing last line item from cart drawer.
        
        await this.#closeDrawerIfCartEmpty(newCartItemCount);
        if(window.location.pathname.includes('cart')) {
          morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
        } else {
          this.#morphCartPartials(parsedResponseText.sections[this.sectionId]);
        }

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
        document.dispatchEvent(new CartSyncedEvent(undefined, this.sectionId, { source: 'cart-items-component' }));
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
